// Comprehensive AI Tools Database with Step-by-Step Instructions

export const toolsDatabase = {
  'Content Creation': [
    {
      name: 'ChatGPT',
      description: 'The most versatile AI for writing blog posts, scripts, social media content, and more. Excellent for brainstorming and long-form content.',
      url: 'https://chat.openai.com',
      category: 'Text Generation',
      rating: 4.8,
      steps: [
        'Go to chat.openai.com and click "Sign up" (or "Log in" if you have an account)',
        'Create a free account using your email, Google, or Microsoft account',
        'Once logged in, you\'ll see a text box at the bottom of the screen',
        'Copy your generated prompt from PromptCraft AI',
        'Paste the entire prompt into the ChatGPT text box',
        'Press Enter or click the send button (arrow icon)',
        'Wait 10-30 seconds for ChatGPT to generate your response',
        'Read the output - if you need changes, just type what you want adjusted',
        'Click "Copy" button on the response to use the content elsewhere',
        'Start a new chat for different topics using the "New chat" button on the left sidebar'
      ]
    },
    {
      name: 'Claude',
      description: 'Outstanding for detailed analysis and nuanced writing. Perfect for research-heavy content and thoughtful articles.',
      url: 'https://claude.ai',
      category: 'Text Generation',
      rating: 4.7,
      steps: [
        'Visit claude.ai in your web browser',
        'Click "Talk to Claude" button on the homepage',
        'Sign up with your email or continue with Google/Apple',
        'Verify your email address (check your inbox)',
        'Once verified, you\'ll be taken to the chat interface',
        'Paste your PromptCraft AI-generated prompt in the message box',
        'Click the paper plane icon to send',
        'Claude will process and respond (may take 15-40 seconds)',
        'Use the "Continue" button if the response seems incomplete',
        'Click the copy icon next to Claude\'s response to copy the text'
      ]
    },
    {
      name: 'Grammarly',
      description: 'AI-powered writing assistant for grammar, spelling, and style improvements.',
      url: 'https://www.grammarly.com',
      category: 'Writing Assistant',
      rating: 4.6,
      steps: [
        'Go to grammarly.com and click "Get Grammarly — It\'s Free"',
        'Sign up with email or Google account',
        'Download the browser extension or desktop app (recommended)',
        'Once installed, go to grammarly.com and click "New Document"',
        'Paste or type your content into the editor',
        'Grammarly will automatically highlight issues',
        'Click on underlined text to see suggestions',
        'Choose which suggestions to apply by clicking on them',
        'Use the overall score on the right to track improvements',
        'Download or copy your polished content when done'
      ]
    }
  ],
  
  'Image Generation': [
    {
      name: 'Google Gemini',
      description: '⭐ TOP PICK - Google\'s powerful AI that generates stunning images directly in chat. Free, fast, and incredibly detailed.',
      url: 'https://gemini.google.com',
      category: 'Image Generation',
      rating: 4.9,
      steps: [
        'Go to gemini.google.com in your web browser',
        'Sign in with your Google account (Gmail)',
        'You\'ll see a chat interface with a message box at the bottom',
        'Simply paste your image generation prompt directly into the message box',
        'Press Enter or click the send button',
        'Wait 15-30 seconds for Gemini to generate your image',
        'The image will appear directly in the chat',
        'Click on the image to view it in full size',
        'Click the download icon (downward arrow) at the bottom of the image to save it',
        'If you want changes, just describe what to modify in your next message',
        'Gemini can generate multiple variations - just ask "create 3 more versions"'
      ]
    },
    {
      name: 'Midjourney',
      description: 'Industry-leading AI art generator for stunning artistic visuals. Produces ultra-high-quality, creative illustrations.',
      url: 'https://www.midjourney.com',
      category: 'Image Generation',
      rating: 4.9,
      steps: [
        'Go to midjourney.com and click "Join the Beta"',
        'This will redirect you to Discord - create a Discord account if you don\'t have one',
        'Accept the Midjourney Discord invite',
        'Look for "newcomer rooms" in the left sidebar (like #newbies-1, #newbies-2)',
        'Click on any newcomer room to enter',
        'In the message box at the bottom, type "/imagine" and press Enter',
        'A prompt box will appear - paste your image prompt here',
        'Press Enter to submit your prompt',
        'Wait 60-90 seconds for Midjourney to generate 4 image variations',
        'Below the images, click U1-U4 to upscale a specific image, or V1-V4 to create variations',
        'Right-click the final image and select "Save Image" to download'
      ]
    },
    {
      name: 'DALL-E 3',
      description: 'OpenAI\'s image generator integrated with ChatGPT. Great for quick, accurate visual representations of your ideas.',
      url: 'https://chat.openai.com',
      category: 'Image Generation',
      rating: 4.7,
      steps: [
        'Go to chat.openai.com and log into your account',
        'Upgrade to ChatGPT Plus if needed (DALL-E 3 requires subscription)',
        'Start a new chat by clicking "+ New chat"',
        'Simply paste your image prompt directly (no special command needed)',
        'ChatGPT will automatically use DALL-E 3 to generate the image',
        'Wait 30-60 seconds for the image to generate',
        'The image will appear in the chat',
        'Click on the image to view it full size',
        'Click the download icon (arrow pointing down) to save the image',
        'If you want changes, describe what to modify in your next message'
      ]
    },
    {
      name: 'Leonardo AI',
      description: 'Powerful AI art generator with fine control over style, composition, and quality. Great for game assets and concept art.',
      url: 'https://leonardo.ai',
      category: 'Image Generation',
      rating: 4.6,
      steps: [
        'Visit leonardo.ai and click "Launch App"',
        'Sign up with email or Google (you get 150 free credits daily)',
        'Complete the onboarding tutorial (click through the steps)',
        'Click "AI Image Generation" from the left sidebar',
        'In the prompt box, paste your image generation prompt',
        'Choose a model from the dropdown (start with "Leonardo Diffusion XL")',
        'Adjust image dimensions if needed (1024x1024 is standard)',
        'Set the number of images (1-8, more images cost more credits)',
        'Click "Generate" button',
        'Wait 10-30 seconds for generation',
        'Click on any image to view full size',
        'Click "Download" button on the image you want to save'
      ]
    }
  ],
  
  'Video Creation': [
    {
      name: 'Runway',
      description: 'Powerful AI video editor and generator. Create professional videos, animations, and visual effects with AI assistance.',
      url: 'https://runwayml.com',
      category: 'Video Generation',
      rating: 4.5,
      steps: [
        'Go to runwayml.com and click "Try Runway for Free"',
        'Sign up with email or Google account',
        'Verify your email if prompted',
        'Once logged in, click "New Project"',
        'Choose "Text/Image to Video" or upload your own video to edit',
        'For Text to Video: Paste your video prompt in the text box',
        'Adjust settings like duration, aspect ratio, and motion strength',
        'Click "Generate" button',
        'Wait 2-5 minutes for video generation (longer videos take more time)',
        'Preview the generated video',
        'Click "Export" to download your video in desired format',
        'Use the editor tools to make additional edits if needed'
      ]
    },
    {
      name: 'Synthesia',
      description: 'Create AI videos with realistic avatars and voiceovers without filming. Perfect for training videos and presentations.',
      url: 'https://www.synthesia.io',
      category: 'Video Generation',
      rating: 4.4,
      steps: [
        'Visit synthesia.io and click "Get Started for Free"',
        'Create an account with your email',
        'Choose a video template or start from scratch',
        'Select an AI avatar from the library',
        'Paste your video script in the script editor',
        'Choose voice language and accent',
        'Customize background, colors, and branding',
        'Add any additional elements (text, images, music)',
        'Click "Generate Video" button',
        'Wait 5-10 minutes for video processing',
        'Review the video and make edits if needed',
        'Download or share your AI-generated video'
      ]
    }
  ],
  
  'Code Development': [
    {
      name: 'GitHub Copilot',
      description: 'AI pair programmer that autocompletes code in your editor. Works with all major programming languages.',
      url: 'https://github.com/features/copilot',
      category: 'Code Assistant',
      rating: 4.7,
      steps: [
        'Visit github.com/features/copilot and click "Start free trial"',
        'Sign in to your GitHub account (or create one)',
        'Start your 30-day free trial',
        'Install Copilot extension in VS Code, Visual Studio, or your preferred IDE',
        'Open VS Code and look for the Copilot icon in the sidebar',
        'Open any code file and start typing',
        'Copilot will suggest completions in gray text',
        'Press Tab to accept a suggestion, or keep typing to ignore it',
        'Use Ctrl+Enter (Cmd+Enter on Mac) to see alternative suggestions',
        'Paste prompts as code comments to get full function implementations'
      ]
    },
    {
      name: 'Replit AI',
      description: 'Online IDE with AI code generation. Write, run, and deploy code entirely in your browser.',
      url: 'https://replit.com',
      category: 'Code Development',
      rating: 4.5,
      steps: [
        'Go to replit.com and click "Sign up"',
        'Create account with email, Google, or GitHub',
        'Click "+ Create Repl" button',
        'Choose your programming language (Python, JavaScript, etc.)',
        'Name your project and click "Create Repl"',
        'You\'ll see a code editor with three panels',
        'Paste your code or type in the editor on the left',
        'Click the "Run" button at the top to execute code',
        'See output in the console panel on the right',
        'Use Replit AI button to get code suggestions and explanations',
        'Click "Share" to publish your project or "Deploy" to host it online'
      ]
    }
  ],
  
  'Digital Marketing': [
    {
      name: 'ChatGPT',
      description: 'Generate ad copy, social media posts, email campaigns, and content calendars. Essential for any marketer.',
      url: 'https://chat.openai.com',
      category: 'Content Creation',
      rating: 4.8,
      steps: [
        'Visit chat.openai.com and sign in to your account',
        'Click "+ New chat" to start fresh',
        'Paste your marketing prompt from PromptCraft AI',
        'Review the generated content',
        'Ask for variations: "Give me 5 different versions"',
        'Request specific formats: "Make it shorter for Twitter" or "Add emojis"',
        'Copy the best version using the copy icon',
        'Use follow-up prompts to refine: "Make it more urgent" or "Add a call-to-action"',
        'Save successful prompts for future use',
        'Test different angles by asking "What are 3 other approaches?"'
      ]
    },
    {
      name: 'Canva AI',
      description: 'Design social media graphics, ads, and marketing materials with AI-powered design tools.',
      url: 'https://www.canva.com',
      category: 'Design',
      rating: 4.7,
      steps: [
        'Go to canva.com and click "Sign up"',
        'Create free account with email, Google, or Facebook',
        'Click "Create a design" and choose template type (Instagram Post, Facebook Ad, etc.)',
        'Browse templates and click one you like',
        'Click any text to edit it',
        'Use "Magic Write" feature to generate copy with AI',
        'Change colors by clicking on elements and using the color picker',
        'Add images by clicking "Elements" or "Uploads"',
        'Use "Magic Edit" to modify images with AI',
        'Click "Share" > "Download" to save your design',
        'Choose format (PNG, JPG, PDF) and click "Download"'
      ]
    }
  ],
  
  'Data Analysis': [
    {
      name: 'ChatGPT (Code Interpreter)',
      description: 'Upload data files and get instant analysis, visualizations, and insights using AI.',
      url: 'https://chat.openai.com',
      category: 'Data Analysis',
      rating: 4.6,
      steps: [
        'Log in to chat.openai.com (requires ChatGPT Plus subscription)',
        'Start a new chat',
        'Enable "Data Analysis" mode from the model selector',
        'Click the paperclip icon to upload your data file (CSV, Excel, JSON)',
        'Paste your data analysis prompt from PromptCraft AI',
        'ChatGPT will analyze your data and generate insights',
        'Ask for visualizations: "Create a bar chart of sales by month"',
        'Download any generated charts or reports',
        'Request additional analysis: "What trends do you see?"',
        'ChatGPT can also clean data, handle missing values, and generate reports'
      ]
    }
  ]
};

// Helper function to get tools by category
export function getToolsByCategory(category) {
  // Map alternative category names
  const categoryMap = {
    'Content Creator': 'Content Creation',
    'Image Generation': 'Image Generation',
    'Video Creation': 'Video Creation',
    'Code Development': 'Code Development',
    'Digital Marketing': 'Digital Marketing',
    'Data Analysis': 'Data Analysis'
  };
  
  const mappedCategory = categoryMap[category] || category;
  return toolsDatabase[mappedCategory] || toolsDatabase['Content Creation'];
}
